// indexDB.js
import { DB_STORE, chunkSize } from "lib/constants";
import { showToast } from "ui/components/common";
class IndexDB {
  private db: any = null;
  public dbVersion: number = 4;
  public dbName: string;
  public dbStores: string[];
  openDB() {
    return new Promise((resolve, reject) => {
      if (this.db !== null) {
        resolve(void 0);
        return;
      }

      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = function (event) {
        reject("Database error: " + event?.target);
      };
      const that = this;
      request.onsuccess = function (event) {
        that.db = request.result;
        console.log("Database opened successfully");
        resolve(void 0);
      };

      request.onupgradeneeded = function (event) {
        that.db = (event.target as IDBOpenDBRequest).result;
        that.dbStores.forEach((store) => {
          if (that.db.objectStoreNames.contains(store)) {
            return;
          }
          const objectStore = that.db.createObjectStore(store, {
            keyPath: "id",
          });

          if (store === "downloads") {
            objectStore.createIndex("recordIndex", "recordId", {
              unique: false,
            });
            objectStore.createIndex("fileIndex", "fileId", { unique: false });
          }
          if (store === DB_STORE.GOOGLE_DRIVE_CACHE) {
            objectStore.createIndex("recordIndex", "recordId", {
              unique: false,
            });
          }
        });
      };
    });
  }

  mergeData(store, recordId, fileId?) {
    return new Promise((resolve, reject) => {
      if (this.db === null) {
        reject("Database not opened");
        return;
      }
      const datas = [];
      const objectStore = this.db
        .transaction([store], "readonly")
        .objectStore(store);
      const recordIndex = objectStore.index("recordIndex");
      //let fileIndex = objectStore.index('fileIndex');

      const recordIdRange = IDBKeyRange.only(recordId);
      //let fileIdRange = IDBKeyRange.only(fileId);
      recordIndex.openCursor(recordIdRange).onsuccess = function (event) {
        const cursor = event.target.result;
        if (cursor) {
          const data = cursor.value;
          if (fileId && data.fileId === fileId) {
            datas.push(data);
            console.log("User matching the query:", data);
          }
          cursor.continue();
        } else {
          datas.sort((a, b) => a.chunk - b.chunk);
          console.log("Users matching the query:", datas);
          resolve(datas);
        }
      };
    });
  }

  deleteMatchData(store, key, value) {
    if (this.db === null) {
      return;
    }
    // 打开读写事务，并获取对象存储空间
    const transaction = this.db.transaction([store], "readwrite");
    const objectStore = transaction.objectStore(store);

    // 使用游标遍历对象存储空间中的数据
    const request = objectStore.openCursor();

    request.onsuccess = function (event) {
      const cursor = event.target.result;
      if (cursor) {
        // 检查条件并删除符合条件的数据
        if (cursor.value[`${key}`] === value) {
          objectStore.delete(cursor.primaryKey);
        }
        cursor.continue(); // 继续遍历下一个记录
      } else {
        console.log("Deletion complete");
      }
    };

    request.onerror = function (event) {
      console.error("Error deleting users");
    };
  }

  addObject(dbStore, data) {
    return new Promise(async (resolve, reject) => {
      if (await this.isExceedQuota()) {
        showToast(
          1,
          "The Chrome storage space is insufficient, please check the remaining space",
        );
        reject("Exceed Quota");
      }
      if (this.db === null) {
        reject("Database not opened");
        return;
      }
      const transaction = this.db.transaction([dbStore], "readwrite");
      const objectStore = transaction.objectStore(dbStore);
      const request = objectStore.add(data);

      request.onsuccess = function (event) {
        console.log("Data added successfully");
        resolve(void 0);
      };

      request.onerror = function (event) {
        reject("Error adding data: " + event.target.errorCode);
      };
    });
  }

  async isExceedQuota() {
    const res = await navigator.storage.estimate();
    return res?.quota - res?.usage <= chunkSize + 10 * 1024 * 1024;
  }

  putObject(dbStore, data) {
    return new Promise(async (resolve, reject) => {
      if (await this.isExceedQuota()) {
        showToast(
          1,
          "The Chrome storage space is insufficient. Please check the remaining space",
        );
        reject("Exceed Quota");
      }
      if (this.db === null) {
        reject("Database not opened");
        return;
      }

      const transaction = this.db.transaction([dbStore], "readwrite");
      const objectStore = transaction.objectStore(dbStore);

      const request = objectStore.put(data);

      request.onsuccess = function () {
        console.debug("Data put successfully");
        resolve(void 0);
      };

      request.onerror = function (event) {
        reject("Error putting data: " + event?.target?.errorCode);
      };
    });
  }

  getCount(dbStore) {
    return new Promise((resolve, reject) => {
      if (this.db === null) {
        reject("Database not opened");
        return;
      }
      const transaction = this.db.transaction([dbStore], "readonly");
      const objectStore = transaction.objectStore(dbStore);
      const countRequest = objectStore.count();

      countRequest.onsuccess = function (event) {
        const count = event?.target?.result;
        resolve(count);
      };
    });
  }

  getAllKeys(dbStore) {
    return new Promise((resolve, reject) => {
      if (this.db === null) {
        reject("Database not opened");
        return;
      }
      const transaction = this.db.transaction([dbStore], "readonly");
      const objectStore = transaction.objectStore(dbStore);
      const request = objectStore.getAllKeys();

      request.onsuccess = function (event) {
        const keys = event.target.result;
        resolve(keys);
      };

      request.onerror = function (event) {
        reject("Error getAllKeys data: " + event?.target?.errorCode);
      };
    });
  }

  delete(dbStore, key) {
    return new Promise((resolve, reject) => {
      if (this.db === null) {
        reject("Database not opened");
        return;
      }
      const transaction = this.db.transaction([dbStore], "readwrite");
      const objectStore = transaction.objectStore(dbStore);

      const request = objectStore.delete(key);

      request.onsuccess = function () {
        console.log("Data deleted successfully");
        resolve(void 0);
      };

      request.onerror = function (event) {
        reject("Error delete data: " + event?.target?.errorCode);
      };
    });
  }

  getObject(dbStore, id) {
    return new Promise((resolve, reject) => {
      if (this.db === null) {
        reject("Database not opened");
        return;
      }
      const transaction = this.db.transaction([dbStore], "readonly");
      const objectStore = transaction.objectStore(dbStore);
      const request = objectStore.get(id);

      request.onsuccess = function () {
        const data = request.result;
        resolve(data);
      };

      request.onerror = function (event) {
        reject("Error retrieving data: " + event?.target?.errorCode);
      };
    });
  }

  clear(dbStore) {
    return new Promise((resolve, reject) => {
      if (this.db === null) {
        reject("Database not opened");
        return;
      }
      const transaction = this.db.transaction([dbStore], "readwrite");
      const objectStore = transaction.objectStore(dbStore);

      // clear data
      const clearRequest = objectStore.clear();
      clearRequest.onsuccess = function () {
        resolve(void 0);
      };
    });
  }

  closeDB() {
    if (this.db !== null) {
      this.db.close();
      console.log("Database connection closed");
      this.db = null;
    }
  }
}

export default IndexDB;
